import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, Injector, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeWhile } from 'rxjs';
import { AppInfo } from '../../../../global/environment';
import { AppointmentPickerComponent } from '../../../../shared/components/appointment-picker/appointment-picker.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton-loader/skeleton-loader.component';
import { FormDirective } from '../../../../shared/directives/form.directive';
import { DateTimeslotPipe } from '../../../../shared/pipes/date-timeslot.pipe';
import { TimeFormatPipe } from '../../../../shared/pipes/time-format.pipe';
import { AppointmentSlot } from '../../../book-claim/types/visit-appointments.types';
import { OrbitFormClaimService } from '../../book-claim/services/orbit-form-claim.service';
import { GAService } from '../service/ga-tags.service';
import { RebookingService } from '../service/rebooking.service';
import { TransformedAvailabilityDate } from '../types/get-rebooking-data-response';
import { DateString, InitialAppointment, RebookingFormValue, SelectedAppointment } from '../types/rebooking-types';

@Component({
  selector: 'app-rebooking',
  templateUrl: './rebooking.component.html',
  styleUrl: './rebooking.component.scss',
  imports: [
    SkeletonLoaderComponent,
    DateTimeslotPipe,
    AppointmentPickerComponent,
    ButtonComponent,
    FormsModule,
    FormDirective,
  ],
  providers: [DatePipe, TimeFormatPipe],
})
export class RebookingComponent implements OnInit, OnDestroy {
  appointmentPickerComponent = viewChild<AppointmentPickerComponent>('appointmentPickerComponent');
  planNumber = signal<string>('');
  formValue = signal<RebookingFormValue | undefined>({ bringForwardFlag: false, selectedSlot: undefined });
  slotValue = signal<AppointmentSlot | undefined>(undefined);
  slotsToExclude = signal<string[]>([]);
  private _destroyed = false;
  formSubmitted = signal(false);
  bringForwardFlagValue = signal<boolean>(false);
  appointmentData = signal<SelectedAppointment | undefined>(undefined);
  initialAppointmentData = signal<InitialAppointment | undefined>(undefined);
  valid = signal(false);
  submittingValue = signal(false);
  private lastAttemptedSlot = signal<AppointmentSlot | undefined>(undefined);

  ngOnDestroy(): void {
    this._destroyed = true;
  }

  ngOnInit(): void {
    this.handleFormValidation();

    const planNumber = this.route.snapshot.params['planNumber'];
    this.planNumber.set(planNumber);
    this.rebookingService.planNumber.set(planNumber);

    const claimId = this.route.snapshot.params['claimId'];
    this.rebookingService.claimId.set(claimId);
  }

  private viewModel = computed(() => {
    const getRebookingData = this.rebookingService.getRebookingData;
    const appointments = getRebookingData.value();
    const dates = Object.values(appointments?.slotsByDate ?? {}).flat();
    const isAllDaySelection = dates.find((i) => i.allDay);
    const errorType = this.rebookingService.errorType();
    return {
      isAllDaySlots: !!isAllDaySelection,
      getRebookingData,
      mmrPageLink: `${AppInfo.Environment.claimsURL}/my-plans/${this.planNumber()}/claims`,
      appointments,
      availabilityStartDate: appointments?.availabilityStartDate as DateString | undefined,
      selectedAppointmentData: this.appointmentData(),
      initialAppointmentData: this.initialAppointmentData(),
      slotValue: this.slotValue(),
      slotsToExclude: this.slotsToExclude(),
      formValid: this.valid(),
      formValue: this.formValue(),
      bringForwardFlagValue: this.bringForwardFlagValue(),
      submittingValue: this.submittingValue(),
      formSubmitted: this.formSubmitted(),
      appointmentPickerComponent: this.appointmentPickerComponent(),
      errorType,
      lastAttemptedSlot: this.lastAttemptedSlot(),
    };
  });

  get vm() {
    return this.viewModel();
  }

  setSelectedSlot(slot: AppointmentSlot | undefined) {
    if (slot) {
      this.rebookingService.clearTimeslotError();
    }
    this.slotValue.set(slot);
  }

  setBringFowardFlag(value: boolean) {
    this.bringForwardFlagValue.set(value);
  }

  setInitialAppointmentData(data: InitialAppointment) {
    this.initialAppointmentData.set(data);
    this.sendPageLoadGATags();
  }

  sendCalendarBlockGATag() {
    this.gaService.sendGATag({
      ...this.gaService.GA_REBOOKING.CALENDAR_BLOCK(),
      eventLabel: `Block:${this.vm.selectedAppointmentData?.block} | Length:${this.vm.selectedAppointmentData?.numberOfActiveDatesThisMonth}| Available:${this.vm.selectedAppointmentData?.numberOfActiveDatesThisMonth}`,
      claimsFirstAvailableDate: this.vm.selectedAppointmentData?.minDate,
    });
  }

  setAppointmentData(appointmentData: SelectedAppointment) {
    // updates appointment data on calendar page change
    this.appointmentData.set(appointmentData);
    this.sendCalendarBlockGATag();
  }

  sendPageLoadGATags() {
    this.gaService.sendGATag({
      ...this.gaService.GA_REBOOKING.PAGE_LOAD(),
      claimsFirstAvailableDate: this.initialAppointmentData()?.minDate,
    });
    this.gaService.sendGATag({
      ...this.gaService.GA_REBOOKING.EVENT_FIRST_DATE(),
      eventLabel: this.initialAppointmentData()?.minDate,
      claimsFirstAvailableDate: this.initialAppointmentData()?.minDate,
    });
    this.gaService.sendGATag({
      ...this.gaService.GA_REBOOKING.EVENT_DAYS_AVAILABLE(),
      eventLabel: this.initialAppointmentData()?.totalActiveDates,
      claimsFirstAvailableDate: this.initialAppointmentData()?.minDate,
    });
  }

  trackContinueClicked() {
    if (!this.vm.formValid) {
      return;
    }
    const initialAppointment = this.initialAppointmentData();
    const updatedAppointment = this.vm.selectedAppointmentData;
    const selectedSlot = this.vm.slotValue;
    const timeslot =
      selectedSlot?.startTime && selectedSlot?.endTime ? `${selectedSlot.startTime}-${selectedSlot.endTime}` : '';
    const eventLabels = [
      `Selected:${this.vm.slotValue?.date || ''}`,
      `ClaimsFirstAvailableDate:${updatedAppointment?.minDate || initialAppointment?.minDate || ''}`,
      `Timeslot:${timeslot}`,
      `TotalAvailable:${updatedAppointment?.totalActiveDates || initialAppointment?.totalActiveDates || ''}`,
      `Block:${updatedAppointment?.block || initialAppointment?.block || ''}`,
      `Length:${updatedAppointment?.numberOfActiveDatesThisMonth || initialAppointment?.numberOfActiveDatesThisMonth || ''}`,
      `Available:${updatedAppointment?.numberOfActiveDatesThisMonth || initialAppointment?.numberOfActiveDatesThisMonth || ''}`,
    ];
    this.gaService.sendGATag({
      ...this.gaService.GA_REBOOKING.CONTINUE(),
      eventLabel: eventLabels.join('| '),
    });
  }

  handleFormValidation(): void {
    toObservable(this.formValue, { injector: this.injector })
      .pipe(takeWhile(() => !this._destroyed))
      .subscribe((v) => {
        if (v) {
          const isValid = v['selectedSlot'] != undefined;
          this.valid.set(isValid);
        }
      });
  }

  submitBooking() {
    this.formSubmitted.set(true);

    if (!this.vm.formValid) {
      return;
    }

    this.submittingValue.set(true);
    this.rebookingService.clearErrorState();

    const selectedSlotId = this.vm.formValue?.['selectedSlot']?.id ?? '';
    if (!selectedSlotId) {
      return;
    }

    this.lastAttemptedSlot.set(this.vm.formValue?.['selectedSlot']);

    this.rebookingService
      .putRebookingData(this.rebookingService.state.claimId, selectedSlotId, '', this.vm.bringForwardFlagValue)
      .subscribe({
        next: () => {
          this.submittingValue.set(false);
          this.router.navigate(
            ['my-plans', this.planNumber(), 'claims', this.rebookingService.state.claimId, 'rebooking-confirmation'],
            {
              replaceUrl: true,
            }
          );
        },
        error: (err: HttpErrorResponse) => {
          this.submittingValue.set(false);
          const errorCode = err?.error?.status;

          if (errorCode === this.rebookingService.timeslotTakenCode) {
            this.rebookingService.errorType.set('timeslot-taken');
            this.slotValue.set(undefined);
            this.formValue.set({
              bringForwardFlag: this.formValue()?.bringForwardFlag ?? false,
              selectedSlot: undefined,
            });
            this.clearSelectedSlot();
            return;
          }

          if (this.rebookingService.errorType() === 'max-retries-exceeded') {
            return;
          }

          this.navigateToGenericErrorPage(err);
        },
      });
  }

  get originalSlotId(): string | undefined {
    // Returns id of the original timeslot
    const currentSlot = this.vm.appointments?.currentAppointment;
    const slotsByDate = this.vm.appointments?.slotsByDate as Record<string, TransformedAvailabilityDate[]>;
    if (currentSlot) {
      const { date, startTime, endTime } = currentSlot;
      return slotsByDate?.[date]?.find((slot) => slot.startTime === startTime && slot.endTime === endTime)?.id;
    }
    return undefined;
  }

  constructor(
    private readonly injector: Injector,
    private readonly rebookingService: RebookingService,
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly gaService: GAService
  ) {
    effect(() => {
      if (this.vm.appointments) {
        const originalSlotId = this.originalSlotId;

        if (originalSlotId && !this.slotsToExclude().includes(originalSlotId)) {
          this.slotsToExclude.update((val) => [...val, originalSlotId]);
        }
      }
    });

    effect(() => {
      const loadError = this.rebookingService.getRebookingData.error();
      if (!loadError) {
        return;
      }
      if (this.rebookingService.errorType() === 'max-retries-exceeded') {
        return;
      }
      this.navigateToGenericErrorPage(loadError as HttpErrorResponse);
    });
  }

  clearSelectedSlot() {
    const appointmentPicker = this.appointmentPickerComponent();
    appointmentPicker?.clearSelectedSlot();
  }

  private navigateToGenericErrorPage(error: HttpErrorResponse): void {
    this.orbitFormClaimService.setFormError(error);
    this.router.navigate(['/', this.planNumber(), 'orbit', 'error']);
  }

  navigateToRepairsPage(): void {
    globalThis.location.href = this.vm.mmrPageLink;
  }
}
